import { jsx as _jsx } from "react/jsx-runtime";
import { useTrackClickedTranscriptWord } from "@air/analytics/src/events/useTrackClickedTranscriptWord";
import classNames from "classnames";
import isNull from "lodash/isNull";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { isInRange, isTimeInSearchResults } from "~/components/AssetModal/shared/components/TranscriptPanel/components/utils/segmentUtils";
import { useTranscriptSearchResultsContext } from "~/providers/TranscriptSearchResultsProvider";
import { useVideoControlContext } from "~/providers/VideoControlsProvider";
import { useVideoTimestampObserverContext } from "~/providers/VideoTimestampObserverProvider";
import { centralizedClipIdSelector } from "~/store/centralizedClip/selectors";
import { useAirStore } from "~/utils/ReduxUtils";
export const TranscriptWord = /*#__PURE__*/ memo((param)=>{
    let { children, item } = param;
    const store = useAirStore();
    const [isSelected, setIsSelected] = useState(false);
    const [isInSearchResults, setIsInSearchResults] = useState(false);
    const elementRef = useRef(null);
    const { subscribe, getCurrentTime } = useVideoTimestampObserverContext();
    const { setCurrentTime } = useVideoControlContext();
    const { subscribeToResultsChange, getSearchResults } = useTranscriptSearchResultsContext();
    const { trackClickedTranscriptWord } = useTrackClickedTranscriptWord();
    useEffect(()=>{
        const timeInSeconds = getCurrentTime();
        if (isNull(timeInSeconds)) {
            return;
        }
        const isTimeInItem = isInRange(Math.round(timeInSeconds * 1000), [
            item.startTimeMs,
            item.endTimeMs
        ]);
        setIsSelected(isTimeInItem);
    }, [
        getCurrentTime,
        item.endTimeMs,
        item.startTimeMs
    ]);
    const checkIfIsInSearchResults = useCallback((searchResults)=>{
        const isInResults = isTimeInSearchResults(searchResults, item.startTimeMs, item.endTimeMs);
        setIsInSearchResults(isInResults);
    }, [
        item.endTimeMs,
        item.startTimeMs
    ]);
    useEffect(()=>{
        const searchResults = getSearchResults();
        checkIfIsInSearchResults(searchResults);
    }, [
        checkIfIsInSearchResults,
        getSearchResults
    ]);
    useEffect(()=>{
        const subscription = subscribeToResultsChange(checkIfIsInSearchResults);
        return ()=>{
            subscription.unsubscribe();
        };
    }, [
        checkIfIsInSearchResults,
        subscribeToResultsChange
    ]);
    useEffect(()=>{
        const subscription = subscribe((timeInSeconds)=>{
            if (isNull(timeInSeconds)) {
                setIsSelected(false);
                return;
            }
            const isTimeInItem = isInRange(Math.round(timeInSeconds * 1000), [
                item.startTimeMs,
                item.endTimeMs
            ]);
            setIsSelected(isTimeInItem);
        });
        return ()=>{
            subscription.unsubscribe();
        };
    }, [
        item.endTimeMs,
        item.startTimeMs,
        subscribe
    ]);
    const onWordClick = useCallback(()=>{
        const clipId = centralizedClipIdSelector(store.getState());
        var _children_toString;
        trackClickedTranscriptWord({
            clip_id: clipId,
            word: (_children_toString = children === null || children === void 0 ? void 0 : children.toString()) !== null && _children_toString !== void 0 ? _children_toString : ""
        });
        setCurrentTime(item.startTimeMs / 1000);
    }, [
        children,
        item.startTimeMs,
        setCurrentTime,
        store,
        trackClickedTranscriptWord
    ]);
    return /*#__PURE__*/ _jsx("button", {
        "data-testid": "TRANSCRIPT_WORD",
        "data-start-time": item.startTimeMs,
        "data-is-selected": isSelected,
        ref: elementRef,
        onClick: onWordClick,
        className: classNames("group/transcript-word select-none rounded-2 px-1 hover:bg-indigo-3", isSelected ? "bg-indigo-8" : ""),
        children: /*#__PURE__*/ _jsx("div", {
            className: classNames("cursor-pointer break-all text-left", !isSelected && isInSearchResults ? "bg-yellow-5 group-hover/transcript-word:bg-transparent" : ""),
            children: children
        })
    });
});
TranscriptWord.displayName = "TranscriptWord";
